/**
 * Lock Buy Feature - Popup functionality for gallery products
 */
class LockBuy {
  constructor() {
    this.popupContainer = null;
    this.selectedVariants = {};
    this.translations = window.lockBuyTranslations || {};
    this.colorMap = window.lockBuyColorMap || {};
    this.init();
  }
  
  translate(key, replacements = {}) {
    let text = this.translations[key] || key;
    
    // Replace any variables in the translation
    Object.keys(replacements).forEach(replaceKey => {
      text = text.replace(new RegExp(`{${replaceKey}}`, 'g'), replacements[replaceKey]);
    });
    
    return text;
  }

  init() {
    document.addEventListener('click', (event) => {
      const galleryItem = event.target.closest('.gallery__item[data-lock-buy-products]');
      if (galleryItem) {
        const productIds = galleryItem.dataset.lockBuyProducts.split(',');
        if (productIds.length > 0) {
          // Get the image that was clicked
          const image = galleryItem.querySelector('.gallery__image img');
          if (image) {
            this.openPopup(image, productIds);
            event.preventDefault();
          }
        }
      }

      // Close popup when clicking the close button
      if (event.target.closest('.lock-buy-popup__close')) {
        this.closePopup();
        event.preventDefault();
      }
    });
  }

  async openPopup(image, productIds) {
    // Clear previous selected variants
    this.selectedVariants = {};
    
    // Create popup if it doesn't exist
    if (!this.popupContainer) {
      this.createPopupContainer();
    }
    
    // Clear previous content
    const contentContainer = this.popupContainer.querySelector('.lock-buy-popup__content');
    contentContainer.innerHTML = '';
    
    // Set loading state
    contentContainer.innerHTML = `<div class="lock-buy-popup__loading">${this.translate('loadingProducts')}</div>`;
    
    // Get column ratio from data attribute
    const galleryItem = image.closest('.gallery__item');
    const columnRatio = galleryItem.dataset.lockBuyRatio || '50-50';
    
    // Apply column ratio class
    this.popupContainer.className = 'lock-buy-popup ratio-' + columnRatio;
    
    // Show popup
    this.popupContainer.classList.add('active');
    document.body.classList.add('lock-buy-popup-active');
    
    // Clone the image for the popup
    const imageContainer = this.popupContainer.querySelector('.lock-buy-popup__image');
    imageContainer.innerHTML = '';
    const clonedImg = image.cloneNode(true);
    imageContainer.appendChild(clonedImg);
    clonedImg.id = 'lock-buy-popup-image';
    
    // Add image zoom functionality for desktop
    if (window.innerWidth >= 768) {
      clonedImg.style.cursor = 'zoom-in';
      clonedImg.addEventListener('click', () => {
        if (clonedImg.classList.contains('zoomed')) {
          clonedImg.classList.remove('zoomed');
          clonedImg.style.cursor = 'zoom-in';
        } else {
          clonedImg.classList.add('zoomed');
          clonedImg.style.cursor = 'zoom-out';
        }
      });
    }
    
    // Fetch products and build product cards
    try {
      const productCards = await this.fetchProducts(productIds);
      contentContainer.innerHTML = '';
      
      // Create product list container
      const productList = document.createElement('div');
      productList.className = 'lock-buy-popup__product-list';
      contentContainer.appendChild(productList);
      
      // Add product cards to the list
      productCards.forEach(card => {
        productList.appendChild(card);
      });
      
      // Add selection summary
      const selectionSummary = document.createElement('div');
      selectionSummary.className = 'lock-buy-popup__selection-summary';
      selectionSummary.innerHTML = `<p class="selection-message">${this.translate('selectAllOptionsMessage')}</p>`;
      contentContainer.appendChild(selectionSummary);
      
      // Add "Add All to Cart" button
      const addAllBtn = document.createElement('button');
      addAllBtn.className = 'lock-buy-popup__add-all-btn btn btn--primary';
      addAllBtn.innerHTML = `<span class="btn-text">${this.translate('addAllToCart')}</span>`;
      addAllBtn.addEventListener('click', () => this.addAllToCart());
      contentContainer.appendChild(addAllBtn);

      // For mobile, add a fixed button at the bottom
      const mobileBtn = document.createElement('button');
      mobileBtn.className = 'lock-buy-popup__mobile-btn btn btn--primary';
      mobileBtn.innerHTML = `<span class="btn-text">${this.translate('buyNow')}</span>`;
      mobileBtn.addEventListener('click', () => this.addAllToCart());
      this.popupContainer.appendChild(mobileBtn);
      
      // Set up observers to update selection summary when variants are selected
      this.setupSelectionObservers(productCards);
      
    } catch (error) {
      contentContainer.innerHTML = `<div class="lock-buy-popup__error">${this.translate('errorLoadingProducts')}</div>`;
      console.error('Error loading products', error);
    }
  }
  
  createPopupContainer() {
    this.popupContainer = document.createElement('div');
    this.popupContainer.className = 'lock-buy-popup';
    
    const closeBtn = document.createElement('button');
    closeBtn.className = 'lock-buy-popup__close';
    closeBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>`;
    closeBtn.setAttribute('aria-label', 'Close');
    
    const contentWrapper = document.createElement('div');
    contentWrapper.className = 'lock-buy-popup__wrapper';
    
    // Create two columns for desktop view (responsive behavior is handled in CSS)
    const imageColumn = document.createElement('div');
    imageColumn.className = 'lock-buy-popup__col lock-buy-popup__image';
    
    const productColumn = document.createElement('div');
    productColumn.className = 'lock-buy-popup__col lock-buy-popup__content';
    
    // Add title for mobile view
    const mobileTitle = document.createElement('div');
    mobileTitle.className = 'lock-buy-popup__mobile-title';
    mobileTitle.textContent = this.translate('shopThisLook');
    productColumn.appendChild(mobileTitle);
    
    contentWrapper.appendChild(imageColumn);
    contentWrapper.appendChild(productColumn);
    
    this.popupContainer.appendChild(closeBtn);
    this.popupContainer.appendChild(contentWrapper);
    
    document.body.appendChild(this.popupContainer);
  }
  
  closePopup() {
    if (this.popupContainer) {
      this.popupContainer.classList.remove('active');
      document.body.classList.remove('lock-buy-popup-active');
    }
  }
  
  async fetchProducts(productIds) {
    const productCards = [];
    
    for (const productId of productIds) {
      try {
        // Fetch product data
        const response = await fetch(`/products/${productId}.js`);
        if (!response.ok) continue;
        
        const product = await response.json();
        const card = this.createProductCard(product);
        productCards.push(card);
        
      } catch (error) {
        console.error(`Error fetching product ${productId}:`, error);
      }
    }
    
    return productCards;
  }
  
  createProductCard(product) {
    const card = document.createElement('div');
    card.className = 'lock-buy-popup__product';
    card.dataset.productId = product.id;
    
    // Create product URL - using product handle if available, otherwise fallback to ID
    const productUrl = product.handle ? `/products/${product.handle}` : `/products/${product.id}`;
    
    // Add product image with link
    if (product.featured_image) {
      const imgContainer = document.createElement('div');
      imgContainer.className = 'lock-buy-popup__product-image';
      
      // Create clickable image with link
      const imageLink = document.createElement('a');
      imageLink.href = productUrl;
      imageLink.target = '_blank';
      imageLink.rel = 'noopener';
      imageLink.className = 'lock-buy-popup__product-link';
      imageLink.innerHTML = `<img src="${product.featured_image}" alt="${product.title}" loading="lazy">`;
      
      imgContainer.appendChild(imageLink);
      card.appendChild(imgContainer);
    }
    
    // Add product details
    const details = document.createElement('div');
    details.className = 'lock-buy-popup__product-details';
    
    // Add product title with link
    const titleContainer = document.createElement('div');
    titleContainer.className = 'lock-buy-popup__product-title-container';
    
    const titleLink = document.createElement('a');
    titleLink.href = productUrl;
    titleLink.target = '_blank';
    titleLink.rel = 'noopener';
    titleLink.className = 'lock-buy-popup__product-title-link';
    
    const title = document.createElement('h3');
    title.className = 'lock-buy-popup__product-title';
    // Add a small external link icon after the title
    title.innerHTML = `${product.title} <svg class="external-link-icon" width="12" height="12" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg"><path d="M5 1v1H2v8h8V7h1v4H1V1h4zm3 0h3v3h-1V2.707L5.854 6.854l-.708-.708L9.293 2H7V1z" fill="currentColor"/></svg>`;
    
    titleLink.appendChild(title);
    titleContainer.appendChild(titleLink);
    details.appendChild(titleContainer);
    
    // Add product price
    const price = document.createElement('div');
    price.className = 'lock-buy-popup__product-price';
    price.innerHTML = this.formatMoney(product.price);
    details.appendChild(price);
    
    // Add variant selector if the product has variants
    if (product.variants.length > 1) {
      // Find option indexes for size and color
      const sizeOptionIndex = product.options.findIndex(option => 
        option.name.toLowerCase() === 'size' || option.name.toLowerCase().includes('size') || option.name.toLowerCase().includes('tamaño')
      );
      
      const colorOptionIndex = product.options.findIndex(option => 
        option.name.toLowerCase() === 'color' || 
        option.name.toLowerCase() === 'colour' || 
        option.name.toLowerCase().includes('color') || 
        option.name.toLowerCase().includes('colour')
      );

      // Create state to store current selections
      const currentSelections = {};
      
      // Add size selector
      if (sizeOptionIndex !== -1) {
        const sizeOption = product.options[sizeOptionIndex];
        
        const variantSelector = document.createElement('div');
        variantSelector.className = 'lock-buy-popup__variant-selector';
        variantSelector.dataset.optionIndex = sizeOptionIndex;
        
        const label = document.createElement('label');
        label.textContent = sizeOption.name + ':';
        variantSelector.appendChild(label);
        
        const optionsContainer = document.createElement('div');
        optionsContainer.className = 'lock-buy-popup__options';
        
        // Create option buttons for sizes
        const sizeValues = sizeOption.values;
        sizeValues.forEach(value => {
          // Check if this size is available in any variant
          const isAvailable = product.variants.some(variant => 
            variant.options[sizeOptionIndex] === value && variant.available
          );
          
          const variantId = this.findVariantId(product, sizeOptionIndex, value);
          
          const option = document.createElement('button');
          option.type = 'button';
          option.className = 'lock-buy-popup__option';
          option.dataset.value = value;
          option.dataset.optionIndex = sizeOptionIndex;
          option.dataset.variantId = variantId || '';
          option.textContent = value;
          
          if (!isAvailable) {
            option.classList.add('unavailable');
            option.disabled = true;
          }
          
          option.addEventListener('click', (e) => {
            // Remove active class from all options in this group
            optionsContainer.querySelectorAll('.lock-buy-popup__option').forEach(opt => {
              opt.classList.remove('active');
            });
            
            // Add active class to clicked option
            e.target.classList.add('active');
            
            // Update current selections
            currentSelections[sizeOptionIndex] = value;
            
            // Update the available options in other selectors
            if (colorOptionIndex !== -1) {
              this.updateOptionAvailability(product, currentSelections, colorOptionIndex, card);
            }
            
            // Find the appropriate variant based on current selections
            const selectedVariantId = this.findVariantIdFromSelections(product, currentSelections);
            if (selectedVariantId) {
              this.selectedVariants[product.id] = Number(selectedVariantId);
            }
            
            // Hide the "Please select" indicator
            const selectIndicator = e.target.closest('.lock-buy-popup__variant-selector').querySelector('.lock-buy-popup__select-indicator');
            if (selectIndicator) {
              selectIndicator.style.display = 'none';
            }
          });
          
          optionsContainer.appendChild(option);
        });
        
        // Add "Please select" indicator
        const selectIndicator = document.createElement('div');
        selectIndicator.className = 'lock-buy-popup__select-indicator';
        selectIndicator.textContent = this.translate('pleaseSelectOption');
        
        variantSelector.appendChild(optionsContainer);
        variantSelector.appendChild(selectIndicator);
        details.appendChild(variantSelector);
      }
      
      // Add color swatches if available
      if (colorOptionIndex !== -1) {
        const colorOption = product.options[colorOptionIndex];
        
        const swatchContainer = document.createElement('div');
        swatchContainer.className = 'lock-buy-popup__swatches';
        swatchContainer.dataset.optionIndex = colorOptionIndex;
        
        const swatchLabel = document.createElement('label');
        swatchLabel.textContent = colorOption.name + ':';
        swatchContainer.appendChild(swatchLabel);
        
        const swatchOptions = document.createElement('div');
        swatchOptions.className = 'lock-buy-popup__swatch-options';
        
        // Create swatch options for colors
        colorOption.values.forEach(value => {
          const isAvailable = product.variants.some(variant => 
            variant.options[colorOptionIndex] === value && variant.available
          );
          
          const swatch = document.createElement('button');
          swatch.type = 'button';
          swatch.className = 'lock-buy-popup__swatch';
          swatch.dataset.value = value;
          swatch.dataset.optionIndex = colorOptionIndex;
          swatch.dataset.swatch = value.toLowerCase();
          swatch.title = value;
          
          // Get color code or image URL for this color
          const colorCodeOrUrl = this.getColorCode(value);
          
          // Check if the color code is a URL
          if (colorCodeOrUrl && (colorCodeOrUrl.startsWith('http://') || colorCodeOrUrl.startsWith('https://'))) {
            // It's an image URL, create an image element
            const img = document.createElement('img');
            img.src = colorCodeOrUrl;
            img.alt = value;
            img.className = 'lock-buy-popup__swatch-img';
            
            // Prevent image clicks from interfering with button behavior
            img.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              swatch.click(); // Trigger the button's click event instead
            });
            
            swatch.appendChild(img);
          } else {
            // It's a color code, set as background color
            swatch.style.backgroundColor = colorCodeOrUrl;
          }
          
          if (!isAvailable) {
            swatch.classList.add('unavailable');
            swatch.disabled = true;
          }
          
          swatch.addEventListener('click', (e) => {
            // Update active state
            swatchOptions.querySelectorAll('.lock-buy-popup__swatch').forEach(sw => {
              sw.classList.remove('active');
            });
            
            // Add active class to the swatch button, not the image
            swatch.classList.add('active');
            
            // Force a repaint to ensure the active state is visible immediately
            swatch.offsetHeight;
            
            // Keep focus on the swatch to maintain the visual state
            swatch.focus();
            
            // Update current selections
            currentSelections[colorOptionIndex] = value;
            
            // Update the available options in other selectors
            if (sizeOptionIndex !== -1) {
              this.updateOptionAvailability(product, currentSelections, sizeOptionIndex, card);
            }
            
            // Find the appropriate variant based on current selections
            const selectedVariantId = this.findVariantIdFromSelections(product, currentSelections);
            if (selectedVariantId) {
              this.selectedVariants[product.id] = Number(selectedVariantId);
            }
            
            // Hide the "Please select" indicator
            const selectIndicator = e.target.closest('.lock-buy-popup__swatches').querySelector('.lock-buy-popup__select-indicator');
            if (selectIndicator) {
              selectIndicator.style.display = 'none';
            }
          });
          
          swatchOptions.appendChild(swatch);
        });
        
        // Add "Please select" indicator
        const selectIndicator = document.createElement('div');
        selectIndicator.className = 'lock-buy-popup__select-indicator';
        selectIndicator.textContent = this.translate('pleaseSelectColor');
        
        swatchContainer.appendChild(swatchOptions);
        swatchContainer.appendChild(selectIndicator);
        details.appendChild(swatchContainer);
      }
    } else if (product.variants.length === 1) {
      // Single variant product
      this.selectedVariants[product.id] = product.variants[0].id;
    }
    
    card.appendChild(details);
    return card;
  }
  
  findVariantId(product, optionIndex, optionValue) {
    const variant = product.variants.find(v => 
      v.options[optionIndex] === optionValue && v.available
    );
    
    return variant ? variant.id : null;
  }
  
  findVariantIdFromSelections(product, selections) {
    // Get all option indexes that have been selected
    const selectedOptionIndexes = Object.keys(selections);
    
    // Find a variant that matches all selected options
    const matchingVariant = product.variants.find(variant => {
      return selectedOptionIndexes.every(optionIndex => {
        return variant.options[optionIndex] === selections[optionIndex];
      }) && variant.available;
    });
    
    return matchingVariant ? matchingVariant.id : null;
  }
  
  updateOptionAvailability(product, currentSelections, targetOptionIndex, productCard) {
    // Get the option values for the target option
    const targetOptionValues = product.options[targetOptionIndex].values;
    
    // Find which options are available given the current selections
    const availableOptions = {};
    
    targetOptionValues.forEach(optionValue => {
      // Create a test selection that includes current selections plus this option
      const testSelections = { ...currentSelections, [targetOptionIndex]: optionValue };
      
      // Check if there's at least one variant with these selections
      const hasMatchingVariant = product.variants.some(variant => {
        // Check if this variant matches all selections
        const selectionKeys = Object.keys(testSelections);
        const isMatch = selectionKeys.every(optionIndex => 
          variant.options[optionIndex] === testSelections[optionIndex]
        );
        
        return isMatch && variant.available;
      });
      
      availableOptions[optionValue] = hasMatchingVariant;
    });
    
    // Update the UI for this option
    const optionButtons = productCard.querySelectorAll(`[data-option-index="${targetOptionIndex}"] button`);
    
    optionButtons.forEach(button => {
      const optionValue = button.dataset.value;
      const isAvailable = availableOptions[optionValue];
      
      if (isAvailable) {
        button.classList.remove('unavailable');
        button.disabled = false;
      } else {
        button.classList.add('unavailable');
        button.disabled = true;
      }
    });
  }
  
  updateSizeOptions(product, colorOptionIndex, colorValue, sizeOptionIndex) {
    // This method is kept for backwards compatibility
    // but now uses the more generic updateOptionAvailability method
    const currentSelections = {
      [colorOptionIndex]: colorValue
    };
    
    const productCard = document.querySelector(`.lock-buy-popup__product[data-product-id="${product.id}"]`);
    if (productCard) {
      this.updateOptionAvailability(product, currentSelections, sizeOptionIndex, productCard);
    }
  }
  
  getColorCode(colorName) {
    const colorMap = {
      'Café': '#332421',
      'Kaki': '#676a43',
      'Marrón': '#7b5f50',
      'Gris plomo': '#435159',
      'Naranja': '#fd4f2b',
      'Rojo': '#dd182f',
      'Negro': '#010101',
      'Coffee': '#332421',
      'Khaki': '#676a43',
      'Brown': '#7b5f50',
      'Lead gray': '#435159',
      'Orange': '#fd4f2b',
      'Red': '#dd182f',
      'Black': '#010101',
      'Malvarosa': '#A35566',
      'Azul posidonia': '#8CB7B3',
      'Arcilla Terrarossa': '#ED8A86',
      'Cuero': '#814025',
      'Algodón crudo': '#E0CDBE',
      'Arcilla': '#BA7063',
      'Natural': '#B6795A',
      'Rojo Corallo': '#8D3838',
      'Multicolor': 'https://cdn.shopify.com/s/files/1/0863/2352/7984/files/Combi_Multicolor.png?v=1748956667',             // Has image
      'Combi Italiana': 'https://cdn.shopify.com/s/files/1/0863/2352/7984/files/combi_italiana.png?v=1748955611',         // Has image
      'Combi Arcilla': 'https://cdn.shopify.com/s/files/1/0863/2352/7984/files/combi_arcilla.png?v=1748955613',          // Has image
      'Mostaza': '#B46019',
      'Combi Terracota': 'https://cdn.shopify.com/s/files/1/0863/2352/7984/files/combi_terracota.png?v=1748955611',        // Has image
      'Combi Portofino': 'https://cdn.shopify.com/s/files/1/0863/2352/7984/files/combi_portofino.png?v=1748955612',        // Has image
      'Combi Champagne': '#E4C4B0', // Placeholder (no image shown)
      'Asfalto': '#1E1E1E',         // Placeholder (assumed)
      'Azul': '#005BD3',
      // new ones
      'Cacao': '#413432',
      'Vino Añejo': '#652F31',
      'Crema': '#ECD5C6',
      'Olmo': '#82452F',
      'Combi Malvarosa': '#AD6385',
      'Combi Orangina': '#C85601',
      'Combi Roja': '#BC092C',
      'Butter': '#E4AF42',
      'Orangina': '#E24300',
      'Añil': '#003194',
      'Café': '#550C10',
      'Atopado': '#E0C2AB',
      'Camel': '#A56944',
      'Maíz': '#B17746',
      'Piña': '#C583D3',
      'Topo Claro': '#E0C2AB',
      'Beige Rosado': '#DDC4BB',
      'Corallo': '#EB3F25',
      'Crudo': '#E1CDBC',
      'Cúrcuma': '#BE804A',
      'Musgo': '#A89265',
      'Posidonia': '#568476',
      'Rosa Empolvado': '#D8BA72',
      'Castaña': '#56252C',
      'Alga': '#676A63',
      'Verde': '#05AA3D',
      'Amarillo': '#FFE500'
    };
  
    return colorMap[colorName] || null;
  }

  
  formatMoney(cents) {
    cents = cents / 100;
    return '€' + cents.toFixed(2);
  }
  
  async addAllToCart() {
    const selectedVariantIds = Object.values(this.selectedVariants).filter(id => id);
    
    if (selectedVariantIds.length === 0) {
      alert(this.translate('selectOptions'));
      return;
    }
    
    // Show loading state
    const addAllBtn = this.popupContainer.querySelector('.lock-buy-popup__add-all-btn');
    const mobileBtn = this.popupContainer.querySelector('.lock-buy-popup__mobile-btn');
    
    // Add loading class and spinner
    const addLoadingSpinner = (button) => {
      button.innerHTML = `<span class="btn-spinner"></span><span class="btn-text">${this.translate('adding')}</span>`;
      button.classList.add('is-loading');
      button.disabled = true;
    };
    
    // Determine which button to show loading state based on viewport width
    const isMobile = window.innerWidth < 768;
    
    if (isMobile && mobileBtn) {
      addLoadingSpinner(mobileBtn);
    } else if (!isMobile && addAllBtn) {
      addLoadingSpinner(addAllBtn);
    }
    
    try {
      // Add items to cart
      const items = selectedVariantIds.map(id => ({ id, quantity: 1 }));
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items })
      });
      
      if (!response.ok) {
        // Try to get the error details from the response
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.description || `Error ${response.status}: ${response.statusText}`);
      }
      
      // Success animation before redirect
      // Use the same logic to update only the visible button
      if (isMobile && mobileBtn) {
        mobileBtn.innerHTML = `<span class="btn-text">${this.translate('addedToCart')}</span>`;
        mobileBtn.classList.remove('is-loading');
        mobileBtn.classList.add('is-success');
      } else if (!isMobile && addAllBtn) {
        addAllBtn.innerHTML = `<span class="btn-text">${this.translate('addedToCart')}</span>`;
        addAllBtn.classList.remove('is-loading');
        addAllBtn.classList.add('is-success');
      }
      
      // Wait a moment before redirecting
      setTimeout(() => {
        // Close popup and redirect to cart
        this.closePopup();
        window.location.href = '/cart';
      }, 800);
      
    } catch (error) {
      console.error('Error adding items to cart:', error);
      
      // Show the specific error message from the server or a fallback message
      const errorMessage = error.message || 'Failed to add items to cart. Please try again.';
      alert(errorMessage);
      
      // Reset button state
      if (isMobile && mobileBtn) {
        mobileBtn.innerHTML = '<span class="btn-text">Buy Now</span>';
        mobileBtn.classList.remove('is-loading');
        mobileBtn.disabled = false;
      } else if (!isMobile && addAllBtn) {
        addAllBtn.innerHTML = '<span class="btn-text">Add All to Cart</span>';
        addAllBtn.classList.remove('is-loading');
        addAllBtn.disabled = false;
      }
    }
  }
  
  setupSelectionObservers(productCards) {
    // Watch for changes in variant selection
    const observerConfig = { attributes: true, subtree: true, childList: true };
    
    const observer = new MutationObserver(() => {
      this.updateSelectionSummary();
    });
    
    // Observe each product card for changes
    productCards.forEach(card => {
      observer.observe(card, observerConfig);
    });
    
    // Initial update
    this.updateSelectionSummary();
  }
  
  updateSelectionSummary() {
    const summaryElement = this.popupContainer.querySelector('.lock-buy-popup__selection-summary');
    if (!summaryElement) return;
    
    const productCards = this.popupContainer.querySelectorAll('.lock-buy-popup__product');
    const totalProducts = productCards.length;
    let selectedProducts = 0;
    
    productCards.forEach(card => {
      const productId = Number(card.dataset.productId);
      if (this.selectedVariants[productId]) {
        selectedProducts++;
      }
    });
    
    // Update the selection summary
    if (selectedProducts === 0) {
      summaryElement.innerHTML = `<p class="selection-message">${this.translate('selectAllOptionsMessage')}</p>`;
      summaryElement.className = 'lock-buy-popup__selection-summary none-selected';
    } else if (selectedProducts < totalProducts) {
      summaryElement.innerHTML = `<p class="selection-message">${this.translate('selectedProducts', {count: selectedProducts, total: totalProducts})}</p>`;
      summaryElement.className = 'lock-buy-popup__selection-summary some-selected';
    } else {
      summaryElement.innerHTML = `<p class="selection-message">${this.translate('allProductsSelected')}</p>`;
      summaryElement.className = 'lock-buy-popup__selection-summary all-selected';
    }
  }
}

// Initialize LockBuy on document ready
document.addEventListener('DOMContentLoaded', () => {
  new LockBuy();
});
